import { useEffect, useRef, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { listLocaisDisponiveis } from "./carga-de-trabalho.gateway";
import type { LocalDisponivel } from "./carga-de-trabalho.types";
import { ALOCACOES_SEARCH_DEBOUNCE_MS } from "./use-alocacoes";

export const LOCAIS_ERROR_MESSAGE = "Não foi possível carregar os locais.";

export interface UseLocaisDisponiveisParams {
  /** Período da alocação da obra escolhida (`yyyy-MM-dd`). */
  dataInicio: string;
  dataFim: string;
  ignorarObraId: string;
  busca: string;
}

export interface LocaisDisponiveisState {
  status: "loading" | "success" | "error";
  locais: LocalDisponivel[];
  errorMessage: string | null;
}

/** Busca os locais candidatos a substituto; digitar na busca dispara a consulta após o debounce. */
export function useLocaisDisponiveis({
  dataInicio,
  dataFim,
  ignorarObraId,
  busca,
}: UseLocaisDisponiveisParams): LocaisDisponiveisState {
  const [state, setState] = useState<LocaisDisponiveisState>({
    status: "loading",
    locais: [],
    errorMessage: null,
  });
  const isFirst = useRef(true);
  const term = busca.trim();

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        isFirst.current = false;
        listLocaisDisponiveis({
          dataInicio,
          dataFim,
          ignorarObraId,
          busca: term,
          signal: controller.signal,
        })
          .then((locais) => {
            if (!cancelled) setState({ status: "success", locais, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;
            setState({
              status: "error",
              locais: [],
              errorMessage: getErrorMessage(error) || LOCAIS_ERROR_MESSAGE,
            });
          });
      },
      isFirst.current ? 0 : ALOCACOES_SEARCH_DEBOUNCE_MS,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [dataInicio, dataFim, ignorarObraId, term]);

  return state;
}
